import {ListItem, ListItemText} from '@mui/material'
import React, {useMemo} from 'react'
import {Box} from '../../webui/Box'
import {Paper} from '../../webui/Paper'
import {LoadingSpinnerContainer} from '../../webui/progress/LoadingSpinnerContainer'
import {Typography} from '../../webui/Typography'
import {useAllBoxesWithoutRentals} from '../box/gql/useAllBoxesWithoutRentals'
import {useAllRentals} from '../rentals/gql/useAllRentals'
import {Filter} from './filter/types'
import {transformFilterToAllRentalsGQLFilter} from './filter/utils/transformFilterToAllRentalsGQLFilter'
import {PostalCodeHeatMap} from './PostalCodeHeatMap'
import {ProductsPie} from './products/ProductsPie'
import {PublicRatings} from './public/PublicRatings'
import {PublicUsersPerBoxTable} from './public/PublicUsersPerBoxTable'
import {RentalsPerBoxPie} from './rentals/RentalsPerBoxPie'
import {RentalsPerGenderPie} from './rentals/RentalsPerGenderPie'
import {RentalTime} from './rentals/RentalTime'
import {RentalTimeOfDay} from './rentals/RentalTimeofDay'
import {RentalsPerAge} from './RentalsPerAge'
import {PifflersPerDay} from './rentals/PifflersPerDay'
import type {Rental} from '../../common/types/rental'
import {RentalFeedbacksTable as PublicRentalFeedbacksTable} from './public/RentalFeedbacksTable'

interface Props {
  filter: Filter
}

export const PublicStatistics: React.FC<Props> = ({filter}) => {
  const {data: allBoxes, loading: allBoxesLoading} = useAllBoxesWithoutRentals()

  const allRentalsVars = useMemo(() => ({filter: transformFilterToAllRentalsGQLFilter(filter)}), [filter])

  const {
    data: {rentals},
    loading,
    error,
  } = useAllRentals({
    variables: allRentalsVars,
    notifyOnNetworkStatusChange: true,
  })

  const averageTime = 7

  const rentalsTimes4 = [...rentals, ...rentals, ...rentals, ...rentals]

  if (loading || allBoxesLoading) return <LoadingSpinnerContainer />

  if (error) {
    const gqlMsgs = Array.isArray((error as any).graphQLErrors)
      ? (error as any).graphQLErrors
          .map((g: {message?: string}) => g.message)
          .filter(Boolean)
          .join(' | ')
      : ''
    const message = error.message || gqlMsgs || 'Unknown error'
    return <div>{message}</div>
  }

  return (
    <Box>
      <Box mt={2} />
      <Paper>
        <Box p={2}>
          <Typography variant="h5">Pifflarna</Typography>
          <ListItem>
            <ListItemText color="textSecondary" primary="Antal pifflare (aktiverade personer)" secondary={rentalsTimes4.length} />
          </ListItem>
          <PublicRatings rentals={rentals} />
        </Box>
      </Paper>

      <Box mt={2} />
      <Paper>
        <Box p={2}>
          <Typography variant="h5">Antal pifflare per dag</Typography>
          <PifflersPerDay rentals={rentals as Rental[]} rollingAverage={averageTime} fromDate={filter?.fromDate} endDate={filter?.endDate} />
        </Box>
      </Paper>

      <Box mt={2} />
      <Paper>
        <Box p={2}>
          <PublicUsersPerBoxTable boxes={allBoxes} rentals={rentalsTimes4} />
        </Box>
      </Paper>

      <Box mt={2} />
      <Paper>
        <Box p={2}>
          <Typography variant="h5">Andel pifflare per box</Typography>
          <RentalsPerBoxPie boxes={allBoxes} rentals={rentalsTimes4} isPublic />
        </Box>
      </Paper>

      <Box mt={2} />
      <Paper>
        <Box p={2}>
          <Typography variant="h5">Andel använda produkter</Typography>
          <ProductsPie rentals={rentalsTimes4} />
        </Box>
      </Paper>

      <Box mt={2} />
      <Paper>
        <Box p={2}>
          <Box m={2}>
            <Typography variant="h5">Tid på dygnet aktiviteten används</Typography>
          </Box>
          <RentalTimeOfDay rentals={rentals} />
        </Box>
      </Paper>

      <Box mt={2} />
      <Paper>
        <Box p={2}>
          <Typography variant="h5">Tid aktiviteten används</Typography>
          <RentalTime rentals={rentalsTimes4} />
        </Box>
      </Paper>

      <Box mt={2} />
      <Paper>
        <Box p={2}>
          <Typography variant="h5">Andel könstillhörighet pifflarna uppgett</Typography>
          <RentalsPerGenderPie rentals={rentals} isPublic />
        </Box>
      </Paper>

      <Box mt={2} />
      <Paper>
        <Box p={2}>
          <Typography variant="h5">Ålder på pifflarna</Typography>
          <RentalsPerAge rentals={rentalsTimes4} />
        </Box>
      </Paper>

      <Box mt={2} />
      <Paper>
        <PublicRentalFeedbacksTable filter={filter} />
      </Paper>

      <Box mt={2} />
      <Paper>
        <PostalCodeHeatMap rentals={rentals} />
      </Paper>
    </Box>
  )
}
